import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { LoadingSpinner } from "PFComponents/loading_spinner";
import { Typography } from "PFComponents/typography";
import { Booking } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BookingQuestions } from "../booking_questions/booking_questions";
import { BookingSelectOption, Messages } from "../booking_questions/booking_questions.types";
import { DeleteMode } from "./delete_booking.types";
import css from "./delete_booking_modal.module.scss";
import { getDeleteMode } from "./get_delete_mode";
import { useBookingSelectOptions } from "./use_booking_select_options";

type RoleRelatedBookingsModalContentProps = {
  booking: Booking;
  userName: string;
  activity: Booking["activity"];
  value: BookingSelectOption;
  onChange: (selectedOption: BookingSelectOption) => void;
};

export const SingleBookingsModalContent = ({
  booking,
  userName,
  activity,
  value,
  onChange
}: RoleRelatedBookingsModalContentProps) => {
  const deleteMode = getDeleteMode(booking.activity_id ?? null, booking.activity_siblings_count);

  const { t } = useTranslation("bookingModule");
  const { deleteOptions, isFetching } = useBookingSelectOptions({
    booking,
    activityId: booking.activity_id ?? undefined
  });
  const isEngagement = useTemplate(activity?.template_id)?.key === TemplateKey.Engagement;

  const messages: Messages = {
    This: t(
      isEngagement
        ? "bookings.delete.body.deleteSingleBookingEngagement"
        : "bookings.delete.body.deleteSingleBookingRole",
      {
        userName,
        activityName: activity?.name
      }
    ),
    Future: t(
      isEngagement
        ? "bookings.delete.body.deleteThisAndFutureBookingsEngagement"
        : "bookings.delete.body.deleteThisAndFutureBookingsRole",
      { userName, activityName: activity?.name }
    ),
    All: t(
      isEngagement
        ? "bookings.delete.body.deleteAllBookingsEngagement"
        : "bookings.delete.body.deleteAllBookingsRole",
      {
        userName,
        activityName: activity?.name
      }
    )
  };

  const modalBody = useMemo(() => {
    switch (deleteMode) {
      case DeleteMode.NoActivityBooking:
        return t("bookings.delete.body.noActivity");
      case DeleteMode.LastBooking:
        return t(
          isEngagement
            ? "bookings.delete.body.deleteSingleBookingEngagement"
            : "bookings.delete.body.deleteSingleBookingRole",
          {
            userName,
            activityName: activity?.name
          }
        );
    }
  }, [deleteMode, t, activity?.name, userName, isEngagement]);

  return (
    <>
      {deleteMode === DeleteMode.MultipleBookings && (
        <>
          {isFetching ? (
            <LoadingSpinner />
          ) : (
            <BookingQuestions
              name="deleteBookingQuestions"
              text={t("bookings.delete.body.question")}
              className={css.roleRelatedOptions}
              messages={messages}
              value={value}
              items={deleteOptions}
              onChange={onChange}
            />
          )}
        </>
      )}
      {!!modalBody && (
        <Typography withMargin variant="bodyRegular">
          {modalBody}
        </Typography>
      )}
    </>
  );
};
